import type { AtemoyaGameFn, LaunchGameCleanup, LaunchGameOptions } from './gameLaunch';
import { fetchWithTimeout } from './fetchWithTimeout';
import { installNonFatalScreenWakeLock } from './installNonFatalScreenWakeLock';

export type LoadedGameModule = {
    moduleUrl: string;
    cleanup: () => void;
};

/**
 * Reads the import maps already registered in the host document.
 * Returns a specifier -> resolved URL map so we don't re-declare existing entries.
 */
function collectDocumentImportMap(): Map<string, string> {
    const existing = new Map<string, string>();
    for (const script of document.querySelectorAll('script[type="importmap"]')) {
        try {
            const parsed = JSON.parse(script.textContent ?? '{}') as {
                imports?: Record<string, string>;
            };
            for (const [specifier, target] of Object.entries(parsed.imports ?? {})) {
                if (typeof target === 'string') existing.set(specifier, target);
            }
        } catch {
            // ignore malformed import maps
        }
    }
    return existing;
}

/**
 * The launcher loads a built game by dynamically importing its `game-launch.js` into
 * the *host* document (e.g. the app shell / play page). That bundle keeps shared
 * framework peers (`react`, `react/jsx-runtime`, `atemoya-client-framework`, …) as
 * bare specifiers that only the game's own index.html import map knows how to resolve.
 */
function injectGameImportMap(doc: Document, baseUrl: URL): void {
    const sourceScript = doc.querySelector('script[type="importmap"]');
    let text = sourceScript?.textContent;
    // Hybrid / unreprepared dists may lack an import map while game-launch.js still
    // externalizes shared peers. Use the published compat alias under /libs/.
    if (!text) {
        // Prefer provider-scoped path used by prepare-game-client-builds; compat alias also exists.
        const compatAcf = `${window.location.origin}/libs/_default/atemoya-client-framework/acf.mjs`;
        text = JSON.stringify({
            imports: {
                'atemoya-client-framework': compatAcf,
                react: compatAcf,
                'react/jsx-runtime': compatAcf,
                'pixi.js': compatAcf,
                tsyringe: compatAcf,
                zustand: compatAcf,
                'zustand/middleware': compatAcf,
                'reflect-metadata': compatAcf,
            },
        });
    }

    let parsed: { imports?: Record<string, string> };
    try {
        parsed = JSON.parse(text) as { imports?: Record<string, string> };
    } catch {
        return;
    }

    const existing = collectDocumentImportMap();
    const additions: Record<string, string> = {};

    for (const [specifier, target] of Object.entries(parsed.imports ?? {})) {
        if (typeof target !== 'string') continue;
        const resolved = new URL(target, baseUrl).href;
        const current = existing.get(specifier);
        if (current === resolved) continue;
        if (current && current !== resolved) {
            console.warn(
                `[loadGameModule] import map already maps "${specifier}" to ${current}; keeping it instead of ${resolved}`
            );
            continue;
        }
        additions[specifier] = resolved;
    }

    if (Object.keys(additions).length === 0) return;

    const script = document.createElement('script');
    script.type = 'importmap';
    script.textContent = JSON.stringify({ imports: additions });
    // Register before the upcoming dynamic import so the new mappings are in effect.
    document.head.prepend(script);
}

/**
 * Fetches built game index.html, injects CSS, resolves the main module URL.
 */
export async function loadGameModule(indexHtmlUrl: string): Promise<LoadedGameModule> {
    // Host-side guard so unrebuilt game-launch.js wakeLock awaits cannot abort /play.
    installNonFatalScreenWakeLock();

    const response = await fetchWithTimeout(indexHtmlUrl);
    if (!response.ok) {
        throw new Error(`Failed to load game bundle: ${indexHtmlUrl} (${response.status})`);
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const baseUrl = new URL(indexHtmlUrl, window.location.href);

    injectGameImportMap(doc, baseUrl);

    const injectedLinks: HTMLLinkElement[] = [];

    const resolveUrl = (href: string) => new URL(href, baseUrl).href;

    const htmlCssHrefs = [...doc.querySelectorAll('link[rel="stylesheet"]')]
        .map((link) => link.getAttribute('href'))
        .filter((href): href is string => Boolean(href));

    const cssHrefs: string[] =
        htmlCssHrefs.length > 0 ? htmlCssHrefs : ['./assets/my-app.css'];

    for (const href of cssHrefs) {
        const el = document.createElement('link');
        el.rel = 'stylesheet';
        el.href = resolveUrl(href);
        document.head.appendChild(el);
        injectedLinks.push(el);
    }

    await Promise.all(
        injectedLinks.map(
            (link) =>
                new Promise<void>((resolve, reject) => {
                    if (link.sheet) {
                        resolve();
                        return;
                    }

                    link.addEventListener('load', () => resolve(), { once: true });
                    link.addEventListener(
                        'error',
                        () => reject(new Error(`Failed to load stylesheet: ${link.href}`)),
                        { once: true }
                    );
                })
        )
    );

    const moduleScripts = doc.querySelectorAll('script[type="module"][src]');
    const launchScript = [...moduleScripts].find((script) => {
        const srcAttr = script.getAttribute('src') ?? '';
        return srcAttr.includes('game-launch');
    });
    let src = launchScript?.getAttribute('src') ?? null;

    // Hybrid dists may still list assets/index-*.js while game-launch.js is the real entry.
    if (!src || !src.includes('game-launch')) {
        const hybridLaunchUrl = resolveUrl('./game-launch.js');
        try {
            const head = await fetchWithTimeout(hybridLaunchUrl, { method: 'HEAD' }, 5000);
            if (head.ok) {
                src = './game-launch.js';
            }
        } catch {
            // fall through to HTML module script
        }
    }

    if (!src) {
        src = moduleScripts[0]?.getAttribute('src') ?? null;
    }
    if (!src) {
        throw new Error(`No module script found in game bundle: ${indexHtmlUrl}`);
    }

    const moduleUrl = resolveUrl(src);

    const cleanup = () => {
        for (const link of injectedLinks) link.remove();
    };

    return { moduleUrl, cleanup };
}

export async function invokeAtemoyaGame(
    moduleUrl: string,
    rootElement: HTMLElement,
    options: LaunchGameOptions
): Promise<LaunchGameCleanup> {
    installNonFatalScreenWakeLock();

    // Dynamic import() is keyed by resolved module URL and always returns the exact
    // namespace object for that URL (browsers never let one module's evaluation
    // overwrite another's exports), so this is safe even when returning to a
    // previously-imported game-launch.js whose top-level side effects won't rerun.
    const mod = (await import(/* @vite-ignore */ moduleUrl)) as { atemoyaGame?: AtemoyaGameFn };
    const launch = mod.atemoyaGame;

    if (typeof launch !== 'function') {
        throw new Error('atemoyaGame is not registered');
    }

    // Keep window.atemoyaGame aligned for single-game dev consumers (devLaunch.ts).
    window.atemoyaGame = launch;
    return launch(rootElement, options);
}
